import React, { useState } from "react"

import { Form, Formik } from "formik"
import { gql } from "urql"
import { pipe, subscribe } from "wonka"

import { AlertLevel } from "~tailwindui/types/enums"

import { AlertMessageBox } from "~tailwindui/Basics"

import { adminClient as client } from "src/urql-client"

import {
  CheckboxField,
  Field,
  FormFailures,
  SubmitButton,
  TextAreaField,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

const EditBudget = ({ budget }) => {
  const [archiving, setArchiving] = useState(false)
  const formState = {
    name: budget.name,
    active: budget.active,
    archiveStudyGroups: false,
    poNumber: budget.poNumber,
    prepaid: budget.prepaid,
    notes: budget.notes,
  }

  const handleSubmit = (values, actions, closeModal) => {
    setArchiving(false)
    const { unsubscribe } = pipe(
      client.subscription(updateBudgetMutation, {
        budgetId: budget.id,
        input: values,
      }),
      subscribe(result => {
        if (result.error) {
          setArchiving(false)
          handleFailure(actions, [{ message: result.error?.message }])
          actions.setSubmitting(false)
          return
        }

        const { status, failures } = result.data.updateBudget
        if (failures.length > 0) {
          setArchiving(false)
          handleFailure(actions, failures)

          actions.setSubmitting(false)
          unsubscribe()
          return
        }

        if (status === "done") {
          actions.setSubmitting(false)
          closeModal()
          unsubscribe()
          window.location.reload()
        } else if (status === "enqueued") {
          actions.setStatus(null)
          setArchiving(true)
        }
      })
    )
  }

  return (
    <ModalWithProvidedBody
      modalTitle="Edit Budget"
      buttonText="Edit"
      buttonClassName="btn"
    >
      {({ closeModal }) => (
        <Formik
          initialValues={formState}
          onSubmit={(values, actions) =>
            handleSubmit(values, actions, closeModal)
          }
        >
          {({ values }) => (
            <Form>
              <div className="modal-body">
                <Field name="name" label="Name" />
                <CheckboxField name="active" label="Active" />
                {budget.active && !values.active && (
                  <CheckboxField
                    name="archiveStudyGroups"
                    label="Archive Study Groups"
                  />
                )}
                <Field name="poNumber" label="PO Number" />
                <CheckboxField name="prepaid" label="Prepaid" />
                <TextAreaField name="notes" label="Notes" />
              </div>
              <div className="modal-footer flex-col">
                <FormFailures />
                {archiving && (
                  <AlertMessageBox level={AlertLevel.Info}>
                    Please wait while study groups are archived. This may take a
                    little while if there are a lot of future sessions to
                    destroy.
                  </AlertMessageBox>
                )}
                <div className="flex w-full justify-between">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <SubmitButton text="Update Budget" />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </ModalWithProvidedBody>
  )
}

const updateBudgetMutation = gql`
  subscription ($budgetId: ID!, $input: BudgetUpdateInputObject!) {
    updateBudget(budgetId: $budgetId, input: $input) {
      status
      budget {
        id
      }
      failures {
        message
      }
    }
  }
`

export default EditBudget
